import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VbzHeaderPlaceholder, VbzButton, VbzInputPassword, VbzAnimation } from '@smiles/voebiz-ui-kit';
import { useAuth0 } from '@auth0/auth0-react';

import ModalMessage from '../../../components/ModalMessage';
import ModalRules from '../../../components/ModalRules';
import { redirectToHome } from '../../../utils/redirectToHome';
import { VoeBizApi } from '../../../services/services';
import { usePasswordValidation } from '../../../hooks/usePasswordValidation';

type TMessage = 'password_hint' | 'system_error' | 'new_password' | 'wrong_password';

const ChangePassword: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const api = VoeBizApi();
  const [password, setPassword] = useState('');
  const passwordValidation = usePasswordValidation(password, 8, 20);
  const [currentPassword, setCurrentPassword] = useState('');
  const [isValidPassword, setIsValidPassword] = useState(true);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValidConfirmPassword, setIsValidConfirmPassword] = useState(true);
  const [isOpenModalRules, setIsOpenModalRules] = useState(false);
  const [message, setMessage] = useState<TMessage>('system_error');
  const [isOpenModalMessage, setIsOpenModalMessage] = useState(false);
  const [currentState, setCurrentState] = useState({
    isLoading: false,
    isSuccess: true,
  });

  const pathHome = useMemo(() => redirectToHome(true), []);

  const { t } = useTranslation('change_password');

  const login = (ref?: string) => {
    const refParam = ref ? `?ref=${ref}` : '';
    loginWithRedirect({
      redirectUri: `${window.location.origin}/validar${refParam}`,
    });
  };

  const handleChangeCurrentPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(e.target.value);
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    return setPassword(e.target.value);
  };

  const handleChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    return setConfirmPassword(e.target.value);
  };

  const checkIsValidPassword = () => {
    const [hasValidLength, hasNumber, hasUpperCase, hasLowerCase, hasSpecialChar, hasNotBarra, hasNotSignal] =
      passwordValidation;
    return hasValidLength && hasNumber && hasUpperCase && hasLowerCase && hasSpecialChar && hasNotBarra && hasNotSignal;
  };

  useEffect(() => {
    setIsValidPassword(password.length < 4 || checkIsValidPassword());
  }, [passwordValidation]);

  useEffect(() => {
    setIsValidConfirmPassword(confirmPassword.length < 8 || password === confirmPassword);
  }, [password, confirmPassword]);

  const messages = useMemo(
    () => ({
      error: [''],
      loading: [`${t('animation_first')}`, `${t('animation_second')}`],
      success: [`${t('animation_finish')}`],
    }),
    [t],
  );

  const handleContinue = async () => {
    setCurrentState({ isLoading: true, isSuccess: false });

    const response = await api.changePassword(password, currentPassword);

    setCurrentState((prev) => ({ ...prev, isSuccess: response.success || false }));
    setTimeout(() => setCurrentState((prev) => ({ ...prev, isLoading: false })), 200);

    if (response.success) {
      setMessage('new_password');
      setIsOpenModalMessage(true);
    } else {
      setPassword('');
      setConfirmPassword('');
      setMessage('system_error');
      setIsOpenModalMessage(true);
    }
  };

  const handleCloseModalMessages = () => {
    if (message === 'new_password') {
      login();
    }
    if (message === 'system_error') {
      window.location.href = `${window.location.origin}/minha-conta/alterar-senha`;
    }
    setIsOpenModalMessage(false);
  };

  const handleKeyPressPassword = (e: React.KeyboardEvent) => {
    if (e.key === '.' || e.key === ',' || e.key === '/' || e.key === ';' || e.key === `"`) {
      e.preventDefault();
    }
  };

  return (
    <>
      <VbzHeaderPlaceholder redirectUrl={pathHome} />
      <div className="voebiz-change_password">
        <aside>
          <div className="vbz-icon-profile" />
        </aside>
        <main>
          <div className="voebiz-main-content">
            <h3>{t('title')}</h3>
            <p>{t('description')}</p>
            <VbzInputPassword
              textLabel={t('current_password')}
              onChange={handleChangeCurrentPassword}
              placeholder="o  o  o  o  o  o  o  o"
              onKeyPress={handleKeyPressPassword}
            />
            <VbzInputPassword
              placeholder="o  o  o  o  o  o  o  o"
              textLabel={t('new_password')}
              onChange={handleChangePassword}
              errorMessageList={[t('invalid_password')]}
              error={!isValidPassword && password.length >= 4 && password.length <= 20}
              onKeyPress={handleKeyPressPassword}
            />
            <VbzInputPassword
              placeholder="o  o  o  o  o  o  o  o"
              textLabel={t('repeat_new_password')}
              onChange={handleChangeConfirmPassword}
              errorMessageList={[t('invalid_confirm_password')]}
              error={!isValidConfirmPassword && confirmPassword.length >= 4 && password.length <= 20}
              onKeyPress={handleKeyPressPassword}
            />
            <VbzButton
              text={t('finish')}
              disabled={
                !(
                  isValidPassword &&
                  isValidConfirmPassword &&
                  password === confirmPassword &&
                  password.length >= 8 &&
                  password.length <= 20 &&
                  currentPassword.length >= 8 &&
                  currentPassword.length <= 20
                )
              }
              onClick={handleContinue}
            />
            <VbzButton color="hyperlink" text={t('security_rules')} onClick={() => setIsOpenModalRules(true)} />
          </div>
        </main>
      </div>
      <ModalRules isOpen={isOpenModalRules} onClose={() => setIsOpenModalRules(false)} />

      <ModalMessage isOpen={isOpenModalMessage} type={message} onClose={handleCloseModalMessages} />

      <VbzAnimation
        containerIdLottie="animation-password"
        currentState={currentState}
        message={messages}
        typeModal="full"
        finishMode={currentState.isSuccess ? 'yes' : 'no'}
        onExit={() => null}
      />
    </>
  );
};

export default ChangePassword;
